import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<span class="command-bar-loading visible">SEARCHING...`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div class=command-bar-error>`),
  _tmpl$3 = /*#__PURE__*/_$template(`<div class=command-bar-error style=text-align:left><div style=margin-bottom:4px>Commands:</div><div style=padding-left:8px>:goto &lt;place|zip|coords|airport&gt; - Navigate to location</div><div style=padding-left:8px>:follow &lt;id&gt; - Follow satellite (NORAD ID) or flight (callsign)</div><div style=padding-left:24px;font-size:0.9em;opacity:0.8>Examples: :follow 25544, :follow UAL123, :follow AA100</div><div style=padding-left:8px>:home - Reset camera view</div><div style=padding-left:8px>:help - Show this help`),
  _tmpl$4 = /*#__PURE__*/_$template(`<div class="command-bar visible"><span class=command-bar-prefix>:</span><input type=text class=command-bar-input placeholder>`);
/**
 * WorldView - Command Bar (SolidJS)
 * Vim-style command input for navigation and control.
 * Port from src/ui/command-bar.ts + src/ui/command-parser.ts
 */

import { createSignal, createEffect, onMount, onCleanup, Show } from "solid-js";
import { ui, setCommandMode } from "../stores/ui";
import { setFollowTarget, setFreeCamera } from "../stores/camera";
import { selectEntity, clearSelection } from "../stores/selection";
import { useCesium } from "../cesium/useCesium";
import { PROXY_ENDPOINTS } from "../config";
import { convertIataToIcao } from "../utils/airline-codes";
import { parseCoordinates, lookupAirport, getAltitudeForType } from "../utils/geocoder";

// Use global Cesium from script tag

// ============================================
// Command Parser
// ============================================

/** Command definitions: [type, hasArgs] */
const COMMANDS = {
  goto: ["goto", true],
  go: ["goto", true],
  follow: ["follow", true],
  home: ["home", false],
  help: ["help", false],
  "?": ["help", false]
};

/** Parse a command string (e.g., "goto austin", "home", "follow 25544") */
export function parseCommand(input) {
  const trimmed = input.trim();
  const lower = trimmed.toLowerCase();
  const spaceIdx = lower.indexOf(" ");

  // Extract command name and potential args
  const cmdName = spaceIdx === -1 ? lower : lower.slice(0, spaceIdx);
  const def = COMMANDS[cmdName];
  if (!def) return null;
  const [type, hasArgs] = def;
  if (!hasArgs) return {
    type
  };
  const args = spaceIdx === -1 ? "" : trimmed.slice(spaceIdx + 1).trim();
  return {
    type,
    args
  };
}
/** Detect if identifier is a satellite NORAD ID (1-5 digits) or flight callsign */
export function detectIdentifierType(id) {
  return /^\d{1,5}$/.test(id) ? "satellite" : "flight";
}

// ============================================
// Geocoding
// ============================================

const GOOGLE_TYPE_MAP = {
  country: "country",
  administrative_area_level_1: "region",
  locality: "city",
  postal_code: "city",
  sublocality: "city",
  street_address: "address",
  route: "address",
  premise: "address",
  point_of_interest: "poi",
  establishment: "poi"
};
function mapGoogleTypeToGeoType(types) {
  for (const t of types) {
    if (GOOGLE_TYPE_MAP[t]) return GOOGLE_TYPE_MAP[t];
  }
  return "city";
}

/** Unified geocode: tries coordinates → airport code → Google API */
async function geocodeLocation(query) {
  const q = query.trim();
  if (!q) return null;

  // 1. Try parsing as coordinates
  const coords = parseCoordinates(q);
  if (coords) return {
    lat: coords.lat,
    lng: coords.lng,
    name: q,
    type: "coords"
  };

  // 2. Try 3-letter airport code
  if (/^[a-zA-Z]{3}$/.test(q)) {
    const airport = lookupAirport(q);
    if (airport) return {
      lat: airport.lat,
      lng: airport.lng,
      name: airport.name,
      type: "airport"
    };
  }

  // 3. Fall back to Google Geocode API
  try {
    const res = await fetch(PROXY_ENDPOINTS.geocode(q));
    if (!res.ok) return null;
    const data = await res.json();
    const result = data.results?.[0];
    const loc = result?.geometry?.location;
    if (!data.ok || !loc?.lat || !loc?.lng) return null;
    return {
      lat: loc.lat,
      lng: loc.lng,
      name: result.formatted_address || q,
      type: mapGoogleTypeToGeoType(result.types || [])
    };
  } catch {
    return null;
  }
}

// ============================================
// Command Bar Component
// ============================================

export function CommandBar() {
  const {
    viewer
  } = useCesium();
  const [input, setInput] = createSignal("");
  const [error, setError] = createSignal("");
  const [loading, setLoading] = createSignal(false);
  const [showHelp, setShowHelp] = createSignal(false);
  let inputRef;

  /** Fly camera to location with oblique pitch compensation */
  function flyTo(lng, lat, height, duration = 2, pitch = -45) {
    const v = viewer();
    if (!v || v.isDestroyed()) return;

    // Offset latitude south so target appears centered at the given pitch angle
    const latOffset = pitch === -90 ? 0 : height / 111000 * Math.tan(Math.abs(pitch) * Math.PI / 180);
    v.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(lng, lat - latOffset, height),
      orientation: {
        heading: 0,
        pitch: Cesium.Math.toRadians(pitch),
        roll: 0
      },
      duration
    });
  }

  // Focus input when command mode becomes active
  createEffect(() => {
    if (ui.commandMode && inputRef) {
      inputRef.focus();
      setInput("");
      setError("");
      setShowHelp(false);
    }
  });
  async function handleGoto(location) {
    if (!location.trim()) {
      setError("Usage: goto <location>");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await geocodeLocation(location);
      setLoading(false);
      if (!result) {
        setError("Location not found");
        console.log("[NAV] Location not found:", location);
        return;
      }
      console.log("[NAV] Flying to", result.name);
      flyTo(result.lng, result.lat, getAltitudeForType(result.type));
      setCommandMode(false);
    } catch (err) {
      setLoading(false);
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(msg.includes("timeout") ? "Request timed out" : msg.includes("fetch") ? "Network error" : msg);
    }
  }
  function handleFollow(identifier) {
    if (!identifier.trim()) {
      setError("Usage: follow <id>");
      return;
    }
    const idType = detectIdentifierType(identifier);
    if (idType === "satellite") {
      const noradId = parseInt(identifier, 10);
      if (!noradId) {
        setError("Invalid NORAD ID");
        return;
      }
      selectEntity("satellite", identifier);
      setFollowTarget("satellite", identifier);
      console.log("[FOLLOW] Following satellite", noradId);
    } else {
      const icaoCallsign = convertIataToIcao(identifier);
      selectEntity("flight", icaoCallsign);
      setFollowTarget("flight", icaoCallsign);
      console.log("[FOLLOW] Following flight", icaoCallsign);
    }
    setCommandMode(false);
  }
  function handleHome() {
    flyTo(0, 0, 15_000_000);
    setFreeCamera();
    clearSelection();
    console.log("[NAV] Flying to Home (0°, 0°)");
    setCommandMode(false);
  }
  function handleHelp() {
    setShowHelp(true);
    setError("");
  }
  async function executeCommand(inputValue) {
    if (!inputValue.trim()) return;
    const cmd = parseCommand(inputValue);
    if (!cmd) {
      setError("Unknown command. Type :help");
      return;
    }
    const handlers = {
      goto: () => handleGoto(cmd.args || ""),
      follow: () => handleFollow(cmd.args || ""),
      home: handleHome,
      help: handleHelp
    };
    await handlers[cmd.type]?.();
  }
  function handleKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      executeCommand(input());
    } else if (e.key === "Escape") {
      e.preventDefault();
      setCommandMode(false);
    } else {
      // Clear error on typing
      setError("");
      setShowHelp(false);
    }
  }
  function handleClickOutside(e) {
    const target = e.target;
    if (!target.closest(".command-bar")) {
      setCommandMode(false);
    }
  }
  onMount(() => {
    document.addEventListener("click", handleClickOutside);
  });
  onCleanup(() => {
    document.removeEventListener("click", handleClickOutside);
  });
  return _$createComponent(Show, {
    get when() {
      return ui.commandMode;
    },
    get children() {
      var _el$ = _tmpl$4(),
        _el$2 = _el$.firstChild,
        _el$3 = _el$2.nextSibling;
      _el$3.$$keydown = handleKeyDown;
      _el$3.$$input = e => setInput(e.currentTarget.value);
      var _ref$ = inputRef;
      typeof _ref$ === "function" ? _$use(_ref$, _el$3) : inputRef = _el$3;
      _$insert(_el$, _$createComponent(Show, {
        get when() {
          return loading();
        },
        get children() {
          return _tmpl$();
        }
      }), null);
      _$insert(_el$, _$createComponent(Show, {
        get when() {
          return _$memo(() => !!error())() && !showHelp();
        },
        get children() {
          var _el$5 = _tmpl$2();
          _$insert(_el$5, error);
          return _el$5;
        }
      }), null);
      _$insert(_el$, _$createComponent(Show, {
        get when() {
          return showHelp();
        },
        get children() {
          var _el$6 = _tmpl$3(),
            _el$7 = _el$6.firstChild,
            _el$8 = _el$7.nextSibling,
            _el$9 = _el$8.nextSibling,
            _el$0 = _el$9.nextSibling,
            _el$1 = _el$0.nextSibling,
            _el$10 = _el$1.nextSibling;
          return _el$6;
        }
      }), null);
      _$effect(_p$ => {
        var _v$ = !!loading(),
          _v$2 = !!(!!error() && !showHelp());
        _v$ !== _p$.e && _el$.classList.toggle("loading", _p$.e = _v$);
        _v$2 !== _p$.t && _el$.classList.toggle("error", _p$.t = _v$2);
        return _p$;
      }, {
        e: undefined,
        t: undefined
      });
      _$effect(() => _el$3.value = input());
      return _el$;
    }
  });
}
export default CommandBar;
_$delegateEvents(["input", "keydown"]);